import { useAtom, useAtomValue } from "jotai";
import { useEffect, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

import { Dialog, DialogContent } from "@/components/podkit/dialog/Dialog";
import { cn } from "@/components/podkit/lib/cn";
import { type TrackLocation } from "@/hooks/use-segment";
import {
    activeTabAtom,
    escapePressCallbackAtom,
    TabsValues,
} from "@/routes/environments/create/create-environment-utils";
import { CreateEnvironmentDetailsLayout } from "@/routes/environments/create/CreateEnvironmentDetailsLayout";

export const CreateEnvironmentModal: FC<{
    open: boolean;
    onClose: () => void;
    disableProjectSelection?: boolean;
    "data-track-location": TrackLocation;
    initialAutoStart?: boolean;
}> = ({ open, onClose, disableProjectSelection, "data-track-location": dataTrackLocation, initialAutoStart }) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useAtom(activeTabAtom);
    const [autoStart, setAutoStart] = useState<boolean>();

    // Track the autostart intent for the current opening of the modal. The
    // parent toggles `open` instead of unmounting us, so we have to clear
    // local state ourselves — otherwise a previous autostart leaks into the
    // next opening and the env auto-starts again as soon as a context URL
    // becomes valid (see CreateEnvironmentDetailsLayout's autostart effect).
    useEffect(() => {
        if (open) {
            setAutoStart(!!initialAutoStart);
        } else {
            setAutoStart(undefined);
            setActiveTab(undefined);
        }
        // setActiveTab is a stable jotai setter; depending on it would just be noise.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialAutoStart]);

    const onOpenChange = (open: boolean) => {
        if (!open) {
            onClose();
            navigate({ hash: "" }, { replace: true });
        }
    };

    const escapePressCallback = useAtomValue(escapePressCallbackAtom);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-track-location={dataTrackLocation}
                className={cn("mt-8 h-auto w-full max-w-[600px] self-start md:mt-28")}
                data-testid="create-environment-modal"
                onEscapeKeyDown={(e) => {
                    if (escapePressCallback) {
                        e.preventDefault();
                        escapePressCallback.fn();
                        return;
                    }

                    if (activeTab !== TabsValues.home && activeTab !== undefined) {
                        e.preventDefault();
                        setActiveTab(TabsValues.home);
                    }
                }}
            >
                <CreateEnvironmentDetailsLayout
                    onClose={onClose}
                    initialContextURL={""}
                    disableProjectSelection={disableProjectSelection}
                    autoStart={autoStart}
                />
            </DialogContent>
        </Dialog>
    );
};
